import { Loader2, Smartphone } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { AberturaDiscussaoWizard } from '@/features/discussoes/components/AberturaDiscussaoWizard';
import { EtapaPagamento } from '@/features/discussoes/components/EtapaPagamento';
import { useFluxoDeAberturaDeDiscussao } from '@/features/discussoes/hooks/useFluxoDeAberturaDeDiscussao';
import { veioDoApp } from '@/features/discussoes/voltarParaOApp';
import { Button } from '@/shared/ui/button';

/**
 * Abrir discussão técnica (F5): duas etapas na mesma tela.
 *
 * 1. `AberturaDiscussaoWizard` — os mesmos 8 passos do app mobile (Paciente →
 *    Assunto → Exames → Dúvidas → Advisor → Revisar e enviar). O rascunho
 *    nasce sozinho no meio do wizard (transição do passo "Assunto" pro passo
 *    "Exames a compartilhar") e evolui a cada passo — ver
 *    `AberturaDiscussaoWizard.tsx`. "Enviar solicitação" no último passo só
 *    chama `aoAbrir`, o rascunho já está completo bem antes disso.
 * 2. Pagamento — só depois de ter um `discussaoId`. Não há como voltar pro
 *    passo 1 depois de criado: o rascunho já existe e pode ser retomado
 *    depois pela tela de detalhe se o médico desistir agora.
 *
 * A checagem de crédito + despacho (pula pra dentro da discussão se tiver
 * crédito, senão mostra pagamento) é `useFluxoDeAberturaDeDiscussao` — a
 * MESMA lógica que `DiscussaoDetalhePage` usa pra retomar um rascunho, pra
 * não divergir em dois lugares.
 */
export function NovaDiscussaoPage() {
  const location = useLocation();
  const doApp = veioDoApp(location.search);

  const { fase, discussaoId, mensagensPendentes, aoAbrir, linkDeVoltaParaOApp } =
    useFluxoDeAberturaDeDiscussao(doApp);

  if (fase === 'concluidoNoApp' && discussaoId) {
    return (
      <div className="max-w-2xl mx-auto py-16 flex flex-col items-center gap-4 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
        <p className="text-sm text-muted-foreground">Voltando para o aplicativo...</p>
        {/* O redirect automático acima nem sempre dispara sozinho dentro do
            navegador in-app do iOS. Sem este botão, a pessoa ficaria olhando
            para um spinner sem saber que a discussão já está aberta. */}
        <Button asChild variant="outline">
          <a href={linkDeVoltaParaOApp(discussaoId)}>
            <Smartphone className="w-4 h-4" />
            Abrir no aplicativo
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Nova discussão técnica</h1>
        {fase === 'pagamento' && (
          <p className="text-sm text-muted-foreground">
            Escolha como pagar para abrir a discussão para o advisor.
          </p>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6">
        {fase === 'liquidando' ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
            <p className="text-sm text-muted-foreground">Abrindo a discussão...</p>
          </div>
        ) : fase === 'pagamento' && discussaoId ? (
          <EtapaPagamento discussaoId={discussaoId} mensagensPendentes={mensagensPendentes} />
        ) : (
          <AberturaDiscussaoWizard aoAbrir={aoAbrir} />
        )}
      </div>
    </div>
  );
}
